// Low-resolution tile-ID pass that tells which streamed tiles have at least one visible pixel.
//
// The drawn tiles are rendered with depth testing into a small off-screen target, each in a colour
// that encodes its index. The pixels are copied into a pixel-pack buffer and read only once a fence
// says the GPU has finished, a few frames later, so the pass never stalls the pipeline. Strategy
// independent: callers pass any indexed triangle geometry, or point blocks, whose first attribute
// block is positions.

export interface HsVisibilityDraw<K> {
  key: K;
  /** Array buffer that starts with tightly packed float32 xyz positions. */
  buffer: WebGLBuffer;
  /** Element buffer of uint32 indices; not used for points. */
  indexBuffer?: WebGLBuffer;
  indexCount: number;
  /**
   * When set, the first `pointCount` positions are drawn as two-pixel points instead of indexed triangles,
   * so a sample about two pixels apart closes a surface.
   */
  pointCount?: number;
  /** Column-major model-to-world matrix. */
  modelMatrix: ArrayLike<number>;
}

export interface HsVisibilityResult<K> {
  /** Every key the pass drew. */
  checked: K[];
  /** Pixels each drawn key covers in the visibility target; keys without pixels are absent. */
  pixels: Map<K, number>;
  /** CPU time spent submitting the pass and decoding its pixels. */
  cpuMs: number;
  /** The part of `cpuMs` spent submitting the draws and the pixel copy. */
  submitMs: number;
}

const VERTEX_SHADER: string = `#version 300 es
layout(location = 0) in vec3 position;
uniform mat4 modelView;
uniform mat4 projection;
void main() {
  gl_Position = projection * modelView * vec4(position, 1.0);
  gl_PointSize = 2.0;
}`;

const FRAGMENT_SHADER: string = `#version 300 es
precision highp float;
uniform vec4 tileId;
out vec4 color;
void main() {
  color = tileId;
}`;

export class HsTileVisibilityPass<K> {
  private readonly program: WebGLProgram;
  private readonly vao: WebGLVertexArrayObject;
  private readonly framebuffer: WebGLFramebuffer;
  private readonly colorTarget: WebGLRenderbuffer;
  private readonly depthTarget: WebGLRenderbuffer;
  private readonly packBuffer: WebGLBuffer;
  private readonly modelViewLocation: WebGLUniformLocation | null;
  private readonly projectionLocation: WebGLUniformLocation | null;
  private readonly tileIdLocation: WebGLUniformLocation | null;
  private width: number = 0;
  private height: number = 0;
  private pending: { fence: WebGLSync; keys: K[]; width: number; height: number; cpuMs: number } | null = null;
  private pixelData: Uint8Array = new Uint8Array(0);
  private disposed: boolean = false;

  constructor(
    private readonly gl: WebGL2RenderingContext,
    /** Width of the visibility target; the height follows the viewport's aspect ratio. */
    private readonly targetWidth: number = 256
  ) {
    this.program = linkProgram(gl, VERTEX_SHADER, FRAGMENT_SHADER);
    this.modelViewLocation = gl.getUniformLocation(this.program, 'modelView');
    this.projectionLocation = gl.getUniformLocation(this.program, 'projection');
    this.tileIdLocation = gl.getUniformLocation(this.program, 'tileId');
    this.vao = required(gl.createVertexArray(), 'vertex array');
    this.framebuffer = required(gl.createFramebuffer(), 'framebuffer');
    this.colorTarget = required(gl.createRenderbuffer(), 'renderbuffer');
    this.depthTarget = required(gl.createRenderbuffer(), 'renderbuffer');
    this.packBuffer = required(gl.createBuffer(), 'buffer');
  }

  /** Whether a pass is waiting for the GPU; a new pass is only started once it has been read. */
  public get busy(): boolean {
    return this.pending !== null;
  }

  /**
   * Draws `draws` from the camera into the visibility target and starts copying the pixels.
   * Restores every piece of WebGL state it changes.
   */
  public submit(
    draws: readonly HsVisibilityDraw<K>[],
    viewMatrix: ArrayLike<number>,
    projectionMatrix: ArrayLike<number>,
    aspect: number
  ): boolean {
    if (this.pending !== null || this.disposed || draws.length === 0 || draws.length >= 0xffffff) {
      return false;
    }
    const started: number = performance.now();
    const gl: WebGL2RenderingContext = this.gl;
    this.resize(this.targetWidth, Math.max(1, Math.round(this.targetWidth / Math.max(aspect, 1e-3))));
    const saved: SavedState = saveState(gl);
    try {
      gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffer);
      gl.viewport(0, 0, this.width, this.height);
      gl.disable(gl.BLEND);
      gl.disable(gl.CULL_FACE);
      gl.disable(gl.SCISSOR_TEST);
      gl.enable(gl.DEPTH_TEST);
      gl.depthFunc(gl.LESS);
      gl.depthMask(true);
      gl.colorMask(true, true, true, true);
      gl.clearColor(0, 0, 0, 0);
      gl.clearDepth(1);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.useProgram(this.program);
      gl.uniformMatrix4fv(this.projectionLocation, false, Float32Array.from(projectionMatrix));
      gl.bindVertexArray(this.vao);
      gl.enableVertexAttribArray(0);
      const modelView: Float32Array = new Float32Array(16);
      draws.forEach((draw: HsVisibilityDraw<K>, index: number) => {
        const id: number = index + 1;
        multiplyColumnMajor(viewMatrix, draw.modelMatrix, modelView);
        gl.uniformMatrix4fv(this.modelViewLocation, false, modelView);
        gl.uniform4f(this.tileIdLocation, (id & 0xff) / 255, ((id >> 8) & 0xff) / 255, ((id >> 16) & 0xff) / 255, 1);
        gl.bindBuffer(gl.ARRAY_BUFFER, draw.buffer);
        gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 0, 0);
        if (draw.pointCount !== undefined) {
          gl.drawArrays(gl.POINTS, 0, draw.pointCount);
        } else if (draw.indexBuffer !== undefined) {
          gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, draw.indexBuffer);
          gl.drawElements(gl.TRIANGLES, draw.indexCount, gl.UNSIGNED_INT, 0);
        }
      });
      gl.bindBuffer(gl.PIXEL_PACK_BUFFER, this.packBuffer);
      // Fresh storage for every copy: Chrome keeps a shadow copy of a READ-usage buffer from each fence until
      // the buffer is re-specified (getBufferSubData does not release it), so copying into the same storage
      // again logs a performance warning per pass.
      gl.bufferData(gl.PIXEL_PACK_BUFFER, this.width * this.height * 4, gl.STREAM_READ);
      gl.readPixels(0, 0, this.width, this.height, gl.RGBA, gl.UNSIGNED_BYTE, 0);
      gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);
      const fence: WebGLSync | null = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0);
      if (fence === null) {
        return false;
      }
      gl.flush();
      this.pending = {
        fence,
        keys: draws.map((draw: HsVisibilityDraw<K>) => draw.key),
        width: this.width,
        height: this.height,
        cpuMs: performance.now() - started,
      };
      return true;
    } finally {
      gl.bindVertexArray(null);
      restoreState(gl, saved);
    }
  }

  /** Returns the result of the pending pass once the GPU has finished it, without waiting. */
  public poll(): HsVisibilityResult<K> | null {
    const pending: HsTileVisibilityPass<K>['pending'] = this.pending;
    if (pending === null) {
      return null;
    }
    const gl: WebGL2RenderingContext = this.gl;
    const status: number = gl.clientWaitSync(pending.fence, 0, 0);
    if (status !== gl.ALREADY_SIGNALED && status !== gl.CONDITION_SATISFIED) {
      return status === gl.WAIT_FAILED ? this.drop() : null;
    }
    const started: number = performance.now();
    gl.deleteSync(pending.fence);
    this.pending = null;
    const length: number = pending.width * pending.height * 4;
    if (this.pixelData.length !== length) {
      this.pixelData = new Uint8Array(length);
    }
    const previous: WebGLBuffer | null = gl.getParameter(gl.PIXEL_PACK_BUFFER_BINDING) as WebGLBuffer | null;
    gl.bindBuffer(gl.PIXEL_PACK_BUFFER, this.packBuffer);
    gl.getBufferSubData(gl.PIXEL_PACK_BUFFER, 0, this.pixelData);
    gl.bindBuffer(gl.PIXEL_PACK_BUFFER, previous);
    const counts: Uint32Array = new Uint32Array(pending.keys.length + 1);
    const data: Uint8Array = this.pixelData;
    for (let offset: number = 0; offset < length; offset += 4) {
      const id: number = data[offset] | (data[offset + 1] << 8) | (data[offset + 2] << 16);
      if (id > 0 && id < counts.length) {
        counts[id]++;
      }
    }
    const pixels: Map<K, number> = new Map<K, number>();
    pending.keys.forEach((key: K, index: number) => {
      if (counts[index + 1] > 0) {
        pixels.set(key, counts[index + 1]);
      }
    });
    return {
      checked: pending.keys,
      pixels,
      cpuMs: pending.cpuMs + performance.now() - started,
      submitMs: pending.cpuMs,
    };
  }

  public dispose(): void {
    const gl: WebGL2RenderingContext = this.gl;
    this.drop();
    this.disposed = true;
    gl.deleteProgram(this.program);
    gl.deleteVertexArray(this.vao);
    gl.deleteFramebuffer(this.framebuffer);
    gl.deleteRenderbuffer(this.colorTarget);
    gl.deleteRenderbuffer(this.depthTarget);
    gl.deleteBuffer(this.packBuffer);
  }

  private drop(): null {
    if (this.pending !== null) {
      this.gl.deleteSync(this.pending.fence);
      this.pending = null;
    }
    return null;
  }

  private resize(width: number, height: number): void {
    if (width === this.width && height === this.height) {
      return;
    }
    const gl: WebGL2RenderingContext = this.gl;
    const previousRenderbuffer: WebGLRenderbuffer | null = gl.getParameter(
      gl.RENDERBUFFER_BINDING
    ) as WebGLRenderbuffer | null;
    const previousFramebuffer: WebGLFramebuffer | null = gl.getParameter(
      gl.FRAMEBUFFER_BINDING
    ) as WebGLFramebuffer | null;
    gl.bindRenderbuffer(gl.RENDERBUFFER, this.colorTarget);
    gl.renderbufferStorage(gl.RENDERBUFFER, gl.RGBA8, width, height);
    gl.bindRenderbuffer(gl.RENDERBUFFER, this.depthTarget);
    gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24, width, height);
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffer);
    gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, this.colorTarget);
    gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, this.depthTarget);
    gl.bindFramebuffer(gl.FRAMEBUFFER, previousFramebuffer);
    gl.bindRenderbuffer(gl.RENDERBUFFER, previousRenderbuffer);
    this.width = width;
    this.height = height;
  }
}

interface SavedState {
  framebuffer: WebGLFramebuffer | null;
  viewport: Int32Array;
  program: WebGLProgram | null;
  vao: WebGLVertexArrayObject | null;
  arrayBuffer: WebGLBuffer | null;
  packBuffer: WebGLBuffer | null;
  blend: boolean;
  cullFace: boolean;
  scissor: boolean;
  depthTest: boolean;
  depthFunc: number;
  depthMask: boolean;
  colorMask: boolean[];
  clearColor: Float32Array;
  clearDepth: number;
}

function saveState(gl: WebGL2RenderingContext): SavedState {
  return {
    framebuffer: gl.getParameter(gl.FRAMEBUFFER_BINDING) as WebGLFramebuffer | null,
    viewport: gl.getParameter(gl.VIEWPORT) as Int32Array,
    program: gl.getParameter(gl.CURRENT_PROGRAM) as WebGLProgram | null,
    vao: gl.getParameter(gl.VERTEX_ARRAY_BINDING) as WebGLVertexArrayObject | null,
    arrayBuffer: gl.getParameter(gl.ARRAY_BUFFER_BINDING) as WebGLBuffer | null,
    packBuffer: gl.getParameter(gl.PIXEL_PACK_BUFFER_BINDING) as WebGLBuffer | null,
    blend: gl.isEnabled(gl.BLEND),
    cullFace: gl.isEnabled(gl.CULL_FACE),
    scissor: gl.isEnabled(gl.SCISSOR_TEST),
    depthTest: gl.isEnabled(gl.DEPTH_TEST),
    depthFunc: gl.getParameter(gl.DEPTH_FUNC) as number,
    depthMask: gl.getParameter(gl.DEPTH_WRITEMASK) as boolean,
    colorMask: gl.getParameter(gl.COLOR_WRITEMASK) as boolean[],
    clearColor: gl.getParameter(gl.COLOR_CLEAR_VALUE) as Float32Array,
    clearDepth: gl.getParameter(gl.DEPTH_CLEAR_VALUE) as number,
  };
}

function restoreState(gl: WebGL2RenderingContext, saved: SavedState): void {
  gl.bindFramebuffer(gl.FRAMEBUFFER, saved.framebuffer);
  gl.viewport(saved.viewport[0], saved.viewport[1], saved.viewport[2], saved.viewport[3]);
  gl.useProgram(saved.program);
  // The element buffer belongs to the vertex array, so rebinding the array restores it too.
  gl.bindVertexArray(saved.vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, saved.arrayBuffer);
  gl.bindBuffer(gl.PIXEL_PACK_BUFFER, saved.packBuffer);
  const toggle = (capability: number, enabled: boolean): void =>
    enabled ? gl.enable(capability) : gl.disable(capability);
  toggle(gl.BLEND, saved.blend);
  toggle(gl.CULL_FACE, saved.cullFace);
  toggle(gl.SCISSOR_TEST, saved.scissor);
  toggle(gl.DEPTH_TEST, saved.depthTest);
  gl.depthFunc(saved.depthFunc);
  gl.depthMask(saved.depthMask);
  gl.colorMask(saved.colorMask[0], saved.colorMask[1], saved.colorMask[2], saved.colorMask[3]);
  gl.clearColor(saved.clearColor[0], saved.clearColor[1], saved.clearColor[2], saved.clearColor[3]);
  gl.clearDepth(saved.clearDepth);
}

/** out = a · b for column-major 4x4 matrices, computed in double precision before narrowing. */
function multiplyColumnMajor(a: ArrayLike<number>, b: ArrayLike<number>, out: Float32Array): void {
  for (let column: number = 0; column < 4; column++) {
    for (let row: number = 0; row < 4; row++) {
      out[column * 4 + row] =
        a[row] * b[column * 4] +
        a[4 + row] * b[column * 4 + 1] +
        a[8 + row] * b[column * 4 + 2] +
        a[12 + row] * b[column * 4 + 3];
    }
  }
}

function linkProgram(gl: WebGL2RenderingContext, vertexSource: string, fragmentSource: string): WebGLProgram {
  const program: WebGLProgram = required(gl.createProgram(), 'program');
  [
    [gl.VERTEX_SHADER, vertexSource],
    [gl.FRAGMENT_SHADER, fragmentSource],
  ].forEach(([type, source]: (number | string)[]) => {
    const shader: WebGLShader = required(gl.createShader(type as number), 'shader');
    gl.shaderSource(shader, source as string);
    gl.compileShader(shader);
    gl.attachShader(program, shader);
    gl.deleteShader(shader);
  });
  gl.linkProgram(program);
  if (gl.getProgramParameter(program, gl.LINK_STATUS) !== true) {
    const log: string | null = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`Tile visibility shader failed to link: ${log ?? 'no log'}.`);
  }
  return program;
}

function required<T>(value: T | null, name: string): T {
  if (value === null) {
    throw new Error(`Unable to create a WebGL ${name} for the tile visibility pass.`);
  }
  return value;
}
